import type { DetectionClass } from './Detector';
import type { HeatmapRenderer } from './HeatmapRenderer';
import type { TrackedObject } from './Tracker';
import type { Trajectory } from './TrajectoryStore';

export interface TrackingRenderOptions {
  mirror: boolean;
  showBoxes: boolean;
  showLabels: boolean;
  showTrajectories: boolean;
  showGhosts: boolean;
  showHeatmap: boolean;
  minScore: number;
  teamColors?: ReadonlyMap<number, string>;
  heatmap?: HeatmapRenderer;
  tags?: ReadonlyMap<number, string>;
}

const PERSON_PALETTE = [
  '#62d9ff',
  '#ff7ad9',
  '#7affa0',
  '#ffd966',
  '#c080ff',
  '#5ee0c9',
  '#ffb347',
  '#ff8a8a',
  '#9dd7ff',
  '#b6ff7a',
  '#ffa8e0',
  '#d7ff6b',
];

const BALL_COLOR = '#ff7a00';

export function colorForTrack(
  id: number,
  cls: DetectionClass,
  teamColors?: ReadonlyMap<number, string>,
): string {
  if (cls === 'ball') return BALL_COLOR;
  const team = teamColors?.get(id);
  if (team) return team;
  return PERSON_PALETTE[id % PERSON_PALETTE.length];
}

export class TrackingRenderer {
  private readonly ctx: CanvasRenderingContext2D;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  draw(
    tracks: readonly TrackedObject[],
    trajectories: readonly Trajectory[],
    opts: TrackingRenderOptions,
  ): void {
    const { width, height } = this.canvas;
    const ctx = this.ctx;

    ctx.save();
    ctx.clearRect(0, 0, width, height);

    if (opts.mirror) {
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
    }

    if (opts.showHeatmap && opts.heatmap) {
      opts.heatmap.draw(ctx, width, height);
    }

    if (opts.showTrajectories) {
      this.drawTrajectories(trajectories, opts, width, height);
    }
    if (opts.showBoxes) {
      this.drawBoxes(tracks, opts, width, height);
    }

    ctx.restore();

    if (opts.showLabels) {
      this.drawLabels(tracks, opts, width, height);
    }
  }

  private drawTrajectories(
    trajectories: readonly Trajectory[],
    opts: TrackingRenderOptions,
    width: number,
    height: number,
  ): void {
    const ctx = this.ctx;
    for (const traj of trajectories) {
      const points = traj.points;
      if (points.length < 2) continue;
      const color = colorForTrack(traj.id, traj.class, opts.teamColors);
      ctx.lineWidth = traj.class === 'ball' ? 3 : 2;
      for (let i = 1; i < points.length; i += 1) {
        const alpha = (i / points.length) * 0.9 + 0.1;
        ctx.strokeStyle = withAlpha(color, alpha);
        ctx.beginPath();
        ctx.moveTo(points[i - 1].x * width, points[i - 1].y * height);
        ctx.lineTo(points[i].x * width, points[i].y * height);
        ctx.stroke();
      }
      const last = points[points.length - 1];
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(last.x * width, last.y * height, traj.class === 'ball' ? 5 : 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawBoxes(
    tracks: readonly TrackedObject[],
    opts: TrackingRenderOptions,
    width: number,
    height: number,
  ): void {
    const ctx = this.ctx;
    for (const t of tracks) {
      if (t.isGhost && !opts.showGhosts) continue;
      if (!t.isGhost && t.box.score < opts.minScore) continue;
      if (t.isGhost) {
        const cx = t.box.x + t.box.w / 2;
        const cy = t.box.y + t.box.h / 2;
        if (cx < -0.05 || cx > 1.05 || cy < -0.05 || cy > 1.05) continue;
      }
      const color = colorForTrack(t.id, t.class, opts.teamColors);
      if (t.isGhost) {
        ctx.setLineDash([6, 4]);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = withAlpha(color, 0.5);
      } else {
        ctx.setLineDash([]);
        ctx.lineWidth = 2;
        ctx.strokeStyle = color;
      }
      ctx.strokeRect(t.box.x * width, t.box.y * height, t.box.w * width, t.box.h * height);
    }
    ctx.setLineDash([]);
  }

  private drawLabels(
    tracks: readonly TrackedObject[],
    opts: TrackingRenderOptions,
    width: number,
    height: number,
  ): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.font = 'bold 12px ui-monospace, Menlo, monospace';
    ctx.textBaseline = 'bottom';
    ctx.textAlign = 'left';
    ctx.lineWidth = 3;
    for (const t of tracks) {
      if (t.isGhost && !opts.showGhosts) continue;
      if (!t.isGhost && t.box.score < opts.minScore) continue;
      if (t.isGhost) {
        const cx = t.box.x + t.box.w / 2;
        const cy = t.box.y + t.box.h / 2;
        if (cx < -0.05 || cx > 1.05 || cy < -0.05 || cy > 1.05) continue;
      }
      const color = colorForTrack(t.id, t.class, opts.teamColors);
      const tag = opts.tags?.get(t.id);
      const label =
        t.class === 'ball'
          ? t.isGhost
            ? 'ball?'
            : 'ball'
          : tag
            ? `${tag}${t.isGhost ? '?' : ''}`
            : `P${t.id}${t.isGhost ? '?' : ''}`;
      const boxX = opts.mirror ? (1 - t.box.x - t.box.w) * width : t.box.x * width;
      const boxY = t.box.y * height;
      const x = boxX + 2;
      const y = boxY - 2;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillStyle = color;
      ctx.strokeText(label, x, y);
      ctx.fillText(label, x, y);
    }
    ctx.restore();
  }
}

function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}
